import React, { useEffect, useMemo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  TablePagination,
  Chip,
  Avatar,
  InputAdornment,
  Skeleton,
  Grid,
  IconButton,
  Tooltip as MuiTooltip,
  ToggleButtonGroup,
  ToggleButton,
  Divider,
} from "@mui/material";
import {
  MdDelete,
  MdEdit,
  MdAdd,
  MdSearch,
  MdVisibility,
  MdBusiness,
  MdEmail,
  MdPhone,
  MdWork,
  MdGridView,
  MdViewList,
  MdRefresh,
  MdClear,
  MdMessage,
} from "react-icons/md";
import toast from "react-hot-toast";
import { getAllForm } from "../apis/Form";
import { brand } from "../theme";

const initials = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("");

const StatCard = ({ icon, label, count, accent }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2.5,
      borderRadius: "20px",
      display: "flex",
      alignItems: "center",
      gap: 2,
      bgcolor: brand.paper,
      border: `1px solid ${brand.slateLight}40`,
      boxShadow: "0 10px 30px -15px rgba(11,10,61,0.06)",
      transition: "transform 200ms ease, box-shadow 200ms ease",
      "&:hover": {
        transform: "translateY(-2px)",
        boxShadow: "0 18px 40px -18px rgba(11,10,61,0.12)",
        borderColor: `${brand.brass}66`,
      },
    }}
  >
    <Box
      sx={{
        width: 46,
        height: 46,
        borderRadius: "14px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: `${accent || brand.brass}18`,
        color: accent || brand.ink950,
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box minWidth={0}>
      <Typography
        sx={{
          fontFamily: '"Fraunces", serif',
          fontWeight: 700,
          fontSize: "1.8rem",
          color: brand.ink950,
          lineHeight: 1.1,
        }}
      >
        {count}
      </Typography>
      <Typography sx={{ color: brand.slate, fontSize: "0.82rem", fontWeight: 600 }} noWrap>
        {label}
      </Typography>
    </Box>
  </Paper>
);

const CorporateEnquiry = () => {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [viewMode, setViewMode] = useState("table"); // 'table' | 'grid'

  const [openDialog, setOpenDialog] = useState(false);
  const [viewLead, setViewLead] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    service: "",
    message: "",
  });

  const fetchEnquiries = async () => {
    setLoading(true);
    try {
      const data = await getAllForm();
      const list = Array.isArray(data) ? [...data].reverse() : [];
      setEnquiries(list);
    } catch (err) {
      toast.error("Failed to fetch corporate enquiries");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEnquiries();
  }, []);

  const stats = useMemo(() => {
    const servicesSet = new Set();
    let emailCount = 0;
    let phoneCount = 0;

    enquiries.forEach((e) => {
      if (e.service) servicesSet.add(e.service);
      if (e.email && e.email.trim() !== "") emailCount += 1;
      if (e.phone && e.phone.trim() !== "") phoneCount += 1;
    });

    return {
      total: enquiries.length,
      uniqueServices: servicesSet.size,
      emailCount,
      phoneCount,
    };
  }, [enquiries]);

  const filteredEnquiries = useMemo(() => {
    if (!search.trim()) return enquiries;
    const q = search.toLowerCase();

    return enquiries.filter((item) => {
      const name = (item.name || "").toLowerCase();
      const email = (item.email || "").toLowerCase();
      const phone = (item.phone || "").toLowerCase();
      const service = (item.service || "").toLowerCase();
      const message = (item.message || "").toLowerCase();

      return name.includes(q) || email.includes(q) || phone.includes(q) || service.includes(q) || message.includes(q);
    });
  }, [enquiries, search]);

  const paginatedEnquiries = useMemo(() => {
    return filteredEnquiries.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [filteredEnquiries, page, rowsPerPage]);

  // Handlers
  const handleOpenForm = (enquiry = null) => {
    setEditing(enquiry);
    setForm(
      enquiry || {
        name: "",
        phone: "",
        email: "",
        service: "",
        message: "",
      }
    );
    setOpenDialog(true);
  };

  const handleCloseForm = () => {
    setOpenDialog(false);
    setEditing(null);
  };

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = () => {
    if (editing) {
      setEnquiries((prev) =>
        prev.map((item) => (item.id === editing.id ? { ...item, ...form } : item))
      );
      toast.success("Enquiry record updated");
    } else {
      const newRecord = {
        id: Date.now(),
        ...form,
        created_at: new Date().toLocaleDateString(),
      };
      setEnquiries((prev) => [newRecord, ...prev]);
      toast.success("New corporate enquiry logged");
    }
    handleCloseForm();
  };

  const handleDelete = (id) => {
    if (!window.confirm("Delete this corporate enquiry?")) return;
    setEnquiries((prev) => prev.filter((e) => e.id !== id));
    toast.success("Enquiry deleted");
  };

  return (
    <Box p={{ xs: 2, sm: 3, md: 4 }} sx={{ maxWidth: 1600, margin: "0 auto" }}>
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <Box
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        gap={2}
        mb={3}
      >
        <Box>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Typography
              variant="h3"
              sx={{
                color: brand.ink950,
                fontFamily: '"Fraunces", serif',
                fontWeight: 700,
                fontSize: { xs: "1.8rem", md: "2.2rem" },
              }}
            >
              Corporate Enquiries
            </Typography>
            <Chip
              label={`${enquiries.length} B2B Leads`}
              size="small"
              sx={{ bgcolor: brand.brassSoft, color: brand.ink950, fontWeight: 700 }}
            />
          </Box>
          <Typography sx={{ color: brand.slate, fontSize: "0.85rem", mt: 0.5 }}>
            Manage corporate partnerships, bulk service requests, and B2B business inquiries
          </Typography>
        </Box>

        <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
          <MuiTooltip title="Reload corporate leads">
            <IconButton
              onClick={fetchEnquiries}
              sx={{
                bgcolor: brand.paper,
                border: `1px solid ${brand.slateLight}60`,
                p: 1.1,
                borderRadius: "14px",
                color: brand.ink950,
                "&:hover": { bgcolor: brand.brassSoft },
              }}
            >
              <MdRefresh size={20} className={loading ? "animate-spin" : ""} />
            </IconButton>
          </MuiTooltip>

          <Button
            variant="contained"
            color="secondary"
            onClick={() => handleOpenForm()}
            startIcon={<MdAdd size={20} />}
            sx={{
              px: 2.5,
              py: 1,
              borderRadius: "14px",
              fontWeight: 700,
              fontSize: "0.88rem",
              boxShadow: "0 8px 20px -6px rgba(201,162,39,0.5)",
            }}
          >
            Log New Enquiry
          </Button>
        </Box>
      </Box>

      {/* ── Stat Cards Grid ─────────────────────────────────────────────── */}
      <Grid container spacing={2.5} mb={3.5}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={<MdBusiness size={24} />} label="Total Corporate Leads" count={stats.total} accent="#3B82F6" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={<MdWork size={24} />} label="Service Categories" count={stats.uniqueServices} accent={brand.brass} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={<MdEmail size={24} />} label="Email Reachability" count={stats.emailCount} accent="#10B981" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<MdPhone size={24} />}
            label="Phone Coverage"
            count={stats.phoneCount}
            accent={brand.ink950}
          />
        </Grid>
      </Grid>

      {/* ── Filter Controls Bar ─────────────────────────────────────────── */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: "20px",
          bgcolor: brand.paper,
          border: `1px solid ${brand.slateLight}40`,
          mb: 3,
        }}
      >
        <Box
          display="flex"
          flexDirection={{ xs: "column", md: "row" }}
          alignItems={{ xs: "stretch", md: "center" }}
          justifyContent="space-between"
          gap={2}
        >
          {/* Search Input */}
          <TextField
            placeholder="Search lead name, email, service, message..."
            size="small"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            sx={{
              width: { xs: "100%", md: 360 },
              "& .MuiOutlinedInput-root": {
                borderRadius: "14px",
                bgcolor: `${brand.slateLight}15`,
                fontSize: "0.85rem",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MdSearch color={brand.slate} size={20} />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch("")}>
                    <MdClear size={16} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />

          {/* View Switcher */}
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, v) => v && setViewMode(v)}
            size="small"
            sx={{
              bgcolor: `${brand.slateLight}20`,
              p: 0.4,
              borderRadius: "12px",
              "& .MuiToggleButton-root": {
                border: "none",
                borderRadius: "9px",
                px: 1.5,
                py: 0.5,
                color: brand.slate,
                "&.Mui-selected": {
                  bgcolor: brand.paper,
                  color: brand.ink950,
                  boxShadow: "0 2px 8px rgba(11,10,61,0.08)",
                },
              },
            }}
          >
            <ToggleButton value="table">
              <MdViewList size={18} />
            </ToggleButton>
            <ToggleButton value="grid">
              <MdGridView size={18} />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Paper>

      {/* ── Table vs Grid View ─────────────────────────────────────────── */}
      {viewMode === "table" ? (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            borderRadius: "22px",
            overflow: "hidden",
            border: `1px solid ${brand.slateLight}40`,
            bgcolor: brand.paper,
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ bgcolor: brand.ink950, color: brand.parchment, fontWeight: 700 }}>#</TableCell>
                <TableCell sx={{ bgcolor: brand.ink950, color: brand.parchment, fontWeight: 700 }}>Contact Person</TableCell>
                <TableCell sx={{ bgcolor: brand.ink950, color: brand.parchment, fontWeight: 700 }}>Phone Number</TableCell>
                <TableCell sx={{ bgcolor: brand.ink950, color: brand.parchment, fontWeight: 700 }}>Email Address</TableCell>
                <TableCell sx={{ bgcolor: brand.ink950, color: brand.parchment, fontWeight: 700 }}>Requested Service</TableCell>
                <TableCell sx={{ bgcolor: brand.ink950, color: brand.parchment, fontWeight: 700 }}>Message Snippet</TableCell>
                <TableCell sx={{ bgcolor: brand.ink950, color: brand.parchment, fontWeight: 700 }}>Logged At</TableCell>
                <TableCell align="right" sx={{ bgcolor: brand.ink950, color: brand.parchment, fontWeight: 700 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: rowsPerPage }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 8 }).map((__, j) => (
                      <TableCell key={j}>
                        <Skeleton variant="text" height={28} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : paginatedEnquiries.length > 0 ? (
                paginatedEnquiries.map((item, idx) => (
                  <TableRow
                    key={item.id ?? `${page}-${idx}`}
                    sx={{ "&:hover": { bgcolor: `${brand.brassSoft}60` } }}
                  >
                    <TableCell
                      sx={{
                        color: brand.slate,
                        fontFamily: '"IBM Plex Mono", monospace',
                        fontSize: "0.82rem",
                        fontWeight: 600,
                      }}
                    >
                      {String(page * rowsPerPage + idx + 1).padStart(2, "0")}
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1.5}>
                        <Avatar
                          sx={{
                            width: 36,
                            height: 36,
                            fontSize: "0.85rem",
                            fontWeight: 700,
                            bgcolor: brand.ink950,
                            color: brand.parchment,
                          }}
                        >
                          {initials(item.name) || <MdBusiness size={18} />}
                        </Avatar>
                        <Typography sx={{ fontWeight: 700, fontSize: "0.92rem", color: brand.ink950 }}>
                          {item.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ color: brand.slate, fontSize: "0.85rem", fontFamily: '"IBM Plex Mono", monospace' }}>
                      {item.phone || "—"}
                    </TableCell>
                    <TableCell sx={{ color: brand.slate, fontSize: "0.85rem" }}>
                      {item.email || "—"}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={item.service || "General Inquiry"}
                        size="small"
                        sx={{ bgcolor: brand.brassSoft, color: brand.ink950, fontWeight: 700, fontSize: "0.75rem" }}
                      />
                    </TableCell>
                    <TableCell sx={{ color: brand.slate, fontSize: "0.82rem", maxWidth: 180 }} noWrap>
                      {item.message || "No notes provided"}
                    </TableCell>
                    <TableCell sx={{ color: brand.slate, fontSize: "0.82rem", fontFamily: '"IBM Plex Mono", monospace' }}>
                      {item.created_at || "—"}
                    </TableCell>
                    <TableCell align="right">
                      <Box display="flex" gap={0.8} justifyContent="flex-end">
                        <MuiTooltip title="Inspect Lead Dossier">
                          <IconButton
                            size="small"
                            onClick={() => setViewLead(item)}
                            sx={{ bgcolor: `${brand.slateLight}25`, color: brand.ink950 }}
                          >
                            <MdVisibility size={16} />
                          </IconButton>
                        </MuiTooltip>
                        <MuiTooltip title="Edit Lead">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenForm(item)}
                            sx={{ bgcolor: `${brand.slateLight}25`, color: brand.ink950 }}
                          >
                            <MdEdit size={16} />
                          </IconButton>
                        </MuiTooltip>
                        <MuiTooltip title="Delete Lead">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(item.id)}
                            sx={{ bgcolor: `${brand.danger}15`, color: brand.danger }}
                          >
                            <MdDelete size={16} />
                          </IconButton>
                        </MuiTooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                    <MdBusiness size={40} color={brand.slateLight} />
                    <Typography
                      sx={{
                        fontFamily: '"Fraunces", serif',
                        fontWeight: 700,
                        fontSize: "1.1rem",
                        color: brand.ink950,
                        mt: 1,
                      }}
                    >
                      No corporate enquiries found
                    </Typography>
                    <Typography sx={{ color: brand.slate, fontSize: "0.85rem", mt: 0.5 }}>
                      {search
                        ? "Try modifying your search criteria."
                        : "Click 'Log New Enquiry' to add your first corporate lead."}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={filteredEnquiries.length}
            page={page}
            onPageChange={(e, n) => setPage(n)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25]}
          />
        </TableContainer>
      ) : (
        /* Grid View */
        <Grid container spacing={3} mb={3}>
          {paginatedEnquiries.map((item, idx) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={item.id ?? idx}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: "22px",
                  bgcolor: brand.paper,
                  border: `1px solid ${brand.slateLight}40`,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  transition: "transform 200ms ease, box-shadow 200ms ease",
                  "&:hover": {
                    transform: "translateY(-3px)",
                    boxShadow: "0 18px 40px -15px rgba(11,10,61,0.12)",
                    borderColor: `${brand.brass}66`,
                  },
                }}
              >
                <Box>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
                    <Avatar
                      sx={{
                        width: 44,
                        height: 44,
                        fontSize: "0.9rem",
                        fontWeight: 700,
                        bgcolor: brand.ink950,
                        color: brand.parchment,
                      }}
                    >
                      {initials(item.name) || <MdBusiness size={22} />}
                    </Avatar>
                    <Chip
                      label={item.service || "General Inquiry"}
                      size="small"
                      sx={{ bgcolor: brand.brassSoft, color: brand.ink950, fontWeight: 700, fontSize: "0.72rem" }}
                    />
                  </Box>

                  <Typography sx={{ fontWeight: 700, fontSize: "1.05rem", color: brand.ink950 }} noWrap>
                    {item.name}
                  </Typography>

                  <Box display="flex" alignItems="center" gap={1} mt={1} color={brand.slate}>
                    <MdEmail size={15} />
                    <Typography sx={{ fontSize: "0.82rem" }} noWrap>
                      {item.email || "—"}
                    </Typography>
                  </Box>

                  <Box display="flex" alignItems="center" gap={1} mt={0.6} color={brand.slate}>
                    <MdPhone size={15} />
                    <Typography sx={{ fontSize: "0.82rem", fontFamily: '"IBM Plex Mono", monospace' }}>
                      {item.phone || "—"}
                    </Typography>
                  </Box>

                  {item.message && (
                    <Box bgcolor={`${brand.slateLight}20`} p={1.2} borderRadius="10px" mt={1.5}>
                      <Typography sx={{ fontSize: "0.78rem", color: brand.ink950 }} noWrap>
                        "{item.message}"
                      </Typography>
                    </Box>
                  )}
                </Box>

                <Box display="flex" gap={1} justifyContent="flex-end" mt={2.5} pt={2} borderTop={`1px solid ${brand.slateLight}30`}>
                  <Button size="small" variant="outlined" onClick={() => setViewLead(item)} startIcon={<MdVisibility />}>
                    Dossier
                  </Button>
                  <Button size="small" variant="outlined" onClick={() => handleOpenForm(item)} startIcon={<MdEdit />}>
                    Edit
                  </Button>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {/* ── Dialog: Corporate Lead Dossier Inspection ───────────────────── */}
      <Dialog
        open={!!viewLead}
        onClose={() => setViewLead(null)}
        PaperProps={{ sx: { borderRadius: "24px", p: 1, width: "100%", maxWidth: 500 } }}
      >
        <DialogTitle sx={{ fontFamily: '"Fraunces", serif', fontWeight: 700, fontSize: "1.4rem" }}>
          Corporate Lead Dossier
        </DialogTitle>
        <DialogContent dividers>
          {viewLead && (
            <Box display="flex" flexDirection="column" gap={2} my={1}>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar
                  sx={{
                    width: 52,
                    height: 52,
                    fontSize: "1.1rem",
                    fontWeight: 700,
                    bgcolor: brand.ink950,
                    color: brand.parchment,
                  }}
                >
                  {initials(viewLead.name) || <MdBusiness size={28} />}
                </Avatar>
                <Box minWidth={0}>
                  <Typography sx={{ fontWeight: 700, fontSize: "1.1rem", color: brand.ink950 }}>
                    {viewLead.name}
                  </Typography>
                  <Chip
                    label={viewLead.service || "General Corporate Service"}
                    size="small"
                    sx={{ bgcolor: brand.brassSoft, color: brand.ink950, fontWeight: 700, mt: 0.5 }}
                  />
                </Box>
              </Box>

              <Divider />

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography sx={{ fontSize: "0.75rem", color: brand.slate, fontWeight: 700 }}>
                    Email Address
                  </Typography>
                  <a href={`mailto:${viewLead.email}`} style={{ color: "#3B82F6", fontSize: "0.85rem", textDecoration: "underline" }}>
                    {viewLead.email || "N/A"}
                  </a>
                </Grid>
                <Grid item xs={6}>
                  <Typography sx={{ fontSize: "0.75rem", color: brand.slate, fontWeight: 700 }}>
                    Phone Number
                  </Typography>
                  <a href={`tel:${viewLead.phone}`} style={{ color: brand.ink950, fontSize: "0.85rem", fontWeight: 700 }}>
                    {viewLead.phone || "N/A"}
                  </a>
                </Grid>
                <Grid item xs={12}>
                  <Typography sx={{ fontSize: "0.75rem", color: brand.slate, fontWeight: 700 }}>
                    Submission Timestamp
                  </Typography>
                  <Typography sx={{ fontSize: "0.85rem", fontFamily: '"IBM Plex Mono", monospace', color: brand.slate }}>
                    {viewLead.created_at || "Date Not Recorded"}
                  </Typography>
                </Grid>
              </Grid>

              <Divider />

              <Box>
                <Typography sx={{ fontSize: "0.75rem", color: brand.slate, fontWeight: 700, mb: 0.8 }}>
                  Corporate Inquiry Message Body
                </Typography>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: "14px",
                    bgcolor: `${brand.slateLight}20`,
                    border: `1px solid ${brand.slateLight}40`,
                  }}
                >
                  <Typography sx={{ fontSize: "0.88rem", color: brand.ink950, whiteSpace: "pre-wrap" }}>
                    {viewLead.message || "No detailed message text recorded."}
                  </Typography>
                </Paper>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setViewLead(null)} variant="contained" color="secondary" sx={{ fontWeight: 700, borderRadius: "10px" }}>
            Close Dossier
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog: Add / Edit Corporate Enquiry ───────────────────────── */}
      <Dialog
        open={openDialog}
        onClose={handleCloseForm}
        PaperProps={{ sx: { borderRadius: "22px", p: 1, width: "100%", maxWidth: 480 } }}
      >
        <DialogTitle sx={{ fontFamily: '"Fraunces", serif', fontWeight: 700, fontSize: "1.35rem" }}>
          {editing ? "Edit Corporate Lead" : "Log Corporate Enquiry"}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={1.5} mt={1}>
            <TextField
              name="name"
              label="Contact / Company Name"
              value={form.name}
              onChange={handleChange}
              fullWidth
            />
            <TextField
              name="phone"
              label="Phone Number"
              value={form.phone}
              onChange={handleChange}
              fullWidth
            />
            <TextField
              name="email"
              label="Email Address"
              value={form.email}
              onChange={handleChange}
              fullWidth
            />
            <TextField
              name="service"
              label="Requested Service"
              value={form.service}
              onChange={handleChange}
              fullWidth
            />
            <TextField
              name="message"
              label="Corporate Requirements / Message"
              value={form.message}
              onChange={handleChange}
              multiline
              rows={3}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={handleCloseForm} sx={{ color: brand.slate, fontWeight: 600 }}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} variant="contained" color="secondary" sx={{ fontWeight: 700, borderRadius: "10px" }}>
            {editing ? "Update Record" : "Create Record"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CorporateEnquiry;
